import type { ParsedToken } from '../../../types/QueryBuilder';
import { parseQuery } from '../parseQuery';

import { antlrToLezer } from './antlrToLezer';
import { parsedQueryToAntlr } from './parsedQueryToAntlr';

/** Serialized form written to / read from the clipboard. */
export type ClipboardDialect = 'lezer' | 'antlr';

/**
 * True when the parsed query contains at least one FILTER (possibly nested inside
 * an EXPRESSION). LOGIC_BOOL alone is not enough — ANTLR queries like `a=1 AND b=2`
 * parse as FREE_TEXT + AND and must still be converted.
 */
export function hasLezerFilterTokens(tokens: ParsedToken[]): boolean {
  return tokens.some((token) => {
    if (token.type === 'FILTER') {
      return true;
    }
    if (token.type === 'EXPRESSION') {
      return hasLezerFilterTokens(token.inner);
    }
    return false;
  });
}

/**
 * Free-text that looks like a broken/empty Lezer filter (`:=:""`, `field:=:`) left
 * over from a failed empty-value conversion or an empty-field insert. Real filters
 * parse as FILTER; observability search terms never use the `:=:` delimiter.
 */
export function isLezerFilterDebris(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.includes(':=:')) {
    return false;
  }
  const tokens = parseQuery(trimmed);
  return tokens.length === 1 && tokens[0].type === 'FREE_TEXT';
}

/** Drop FREE_TEXT tokens that are lezer-filter debris so they cannot ride along on copy/paste. */
function stripLezerFilterDebris(query: string): string {
  const tokens = parseQuery(query);
  const kept = tokens.filter((token) => !(token.type === 'FREE_TEXT' && isLezerFilterDebris(token.text)));
  if (kept.length === tokens.length) {
    return query.trim();
  }
  return kept
    .map((token) => token.text)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Serialize a Lezer query string for the clipboard.
 * `antlr` emits observability search syntax (`field=value`); free-text terms are kept.
 */
export function serializeClipboardQuery(lezerQuery: string, dialect: ClipboardDialect = 'lezer'): string {
  const cleaned = stripLezerFilterDebris(lezerQuery);
  if (dialect === 'lezer') {
    return cleaned;
  }
  const parts = parseQuery(cleaned)
    .map((token) => {
      if (token.type === 'FREE_TEXT') {
        return token.text.trim();
      }
      return parsedQueryToAntlr([token]);
    })
    .filter(Boolean);
  return parts.join(' ');
}

/**
 * Normalize clipboard text into a Lezer query the builder can insert.
 * Already-structured Lezer is kept; otherwise observability ANTLR is converted.
 */
export function parseClipboardQuery(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return trimmed;
  }
  let result: string;
  if (hasLezerFilterTokens(parseQuery(trimmed))) {
    result = trimmed;
  } else {
    const converted = antlrToLezer(trimmed);
    if (converted && hasLezerFilterTokens(parseQuery(converted))) {
      result = converted;
    } else {
      result = trimmed;
    }
  }
  return stripLezerFilterDebris(result);
}
